<template>
  <b-card>
    <h3 class="mb-3">
      {{
        $t(
          "reports.compare_to_last_year",
          "Compare at this hour of campaign, where are you holding compared to last year"
        )
      }}
    </h3>
    <p>
      {{
        $t(
          "reports.compare_to_last_year_description",
          "Select a past campaign to compare with the current campaign. The report will show statistics for the current campaign and the selected past campaigns."
        )
      }}
    </p>
    <div class="controls-row">
      <b-form-group
        v-if="pastCampaignListProp.length == 0"
        :label="$t('reports.past_campaign_title', 'Select past campaign')"
        class="form-unit--multiselect tw-w-full"
      >
        <SelectCampaign
          v-model="pastCampaignList"
          default-styles
          general
          :multiple="true"
          :taggable="true"
          :allow-empty="true"
          track-by="id"
          :per-page="10"
        />
      </b-form-group>

      <b-button
        pill
        class="tw-mt-3 bttn--orange"
        :disabled="!pastCampaignList.length"
        @click="fetch"
      >
        <b-icon icon="arrow-repeat" class="me-1" />
      </b-button>
    </div>

    <div v-if="stats.current_campaign_stats">
      <h4 class="mt-3 mb-2">
        {{ $t("reports.current_campaign_stats_title", "Current campaign stats") }}
      </h4>
      <p>{{ getCurentCampaignStatsText() }}</p>
    </div>

    <div v-if="stats.last_year_campaign_stats.length">
      <h4 class="mt-3 mb-2">
        {{ $t("reports.last_year_campaign_stats_title", "Last year campaign stats") }}
      </h4>
      <ul>
        <li v-for="(text, index) in getLastYearCampaignStats()" :key="index">{{ text }}</li>
      </ul>
    </div>
  </b-card>
</template>

<script>
import SelectCampaign from "@/views/dashboard/subcomponents/selectCampaign.vue";

import { useCompareToLastYear } from "@/views/dashboard/compositions/compare-to-last-year";

const { fetchCompareToLastYear } = useCompareToLastYear();

/**
 * @typedef {Object} CampaignStats
 * @property {number} campaign_id - The ID of the campaign.
 * @property {string} campaign_start_date - The start date of the campaign in ISO format.
 * @property {string} currency_code - The currency code used in the campaign.
 * @property {number} total - The total amount raised in the campaign.
 * @property {number} total_donors - The total number of donors in the campaign.
 * @property {number} real_payment - The actual payment amount in the campaign.
 * @property {number} [after_total] - The total amount raised in the campaign after a specific event or time.
 * @property {number} [after_total_donors] - The total number of donors in the campaign after a specific event or time.
 * @property {number} [after_real_payment] - The actual payment amount in the campaign after a specific event or time.
 */

export default {
  name: "CompareToLastYear",
  components: {
    SelectCampaign,
  },
  props: {
    campaignId: {
      type: [String, Number],
      required: true,
    },
    pastCampaignListProp: {
      type: Array,
      default: () => [],
    },
  },
  /**
   * @returns {Object} - The component's data object.
   * @property {Array} pastCampaignList - List of past campaigns selected for comparison.
   * @property {Object} stats - Statistics for the current campaign and last year's campaign.
   * @property {CampaignStats|null} stats.current_campaign_stats - Statistics for the current campaign
   * @property {Array<CampaignStats>} stats.last_year_campaign_stats - List of statistics for last year's campaigns.
   * @property {Object|null} meta - Metadata related to the comparison, such as pagination or additional info.
   */
  data() {
    return {
      pastCampaignList: [],
      stats: {
        current_campaign_stats: null,
        last_year_campaign_stats: [],
      },
      meta: null,
    };
  },
  computed: {
    oid() {
      return localStorage.getItem("orgId");
    },
  },
  watch: {
    campaignId() {
      // Reset stats when the campaign ID changes
      this.stats = {
        current_campaign_stats: null,
        last_year_campaign_stats: [],
      };
      this.meta = null;
      this.pastCampaignList = [];
    },
    pastCampaignListProp: {
      immediate: true,
      handler(newVal) {
        this.pastCampaignList = newVal;
      },
    },
  },
  methods: {
    getCurentCampaignStatsText() {
      if (!this.stats.current_campaign_stats) {
        return this.$t("reports.no_campaign_stats", "No stats available.");
      }

      const { total, total_donors, currency_code } = this.stats.current_campaign_stats;

      // Nov 16 in the AM that currently you have 1000 donors and a 52%
      return this.$t(
        "reports.current_campaign_stats",
        "At {nowDate} {nowTime} that currently you have {total_donors} donors and a {total} total.",
        {
          nowDate: new Date().toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
          }),
          nowTime: new Date().toLocaleTimeString(undefined, {
            hour: "2-digit",
            minute: "2-digit",
          }),
          total_donors: new Intl.NumberFormat().format(total_donors),
          total: new Intl.NumberFormat(undefined, {
            style: "currency",
            currency: currency_code || "USD",
          }).format(total),
        }
      );
    },

    getLastYearCampaignStats() {
      if (!this.stats.last_year_campaign_stats.length) {
        return [this.$t("reports.no_last_year_stats", "No last year stats available.")];
      }

      const res = [];
      const { duration_sec } = this.meta || {};

      for (const stats of this.stats.last_year_campaign_stats) {
        const {
          campaign_id,
          total,
          total_donors,
          currency_code,
          campaign_start_date,
          after_total,
          after_total_donors,
        } = stats;

        const pastCampaignDate = new Date(campaign_start_date);

        pastCampaignDate.setSeconds(pastCampaignDate.getSeconds() + duration_sec);

        const pastCampaignDateFmt = pastCampaignDate.toLocaleDateString(undefined, {
          month: "short",
          day: "numeric",
        });

        const pastCampaignTimeFmt = pastCampaignDate.toLocaleTimeString(undefined, {
          hour: "2-digit",
          minute: "2-digit",
        });

        const numFmt = new Intl.NumberFormat();
        const currencyFmt = new Intl.NumberFormat(undefined, {
          style: "currency",
          currency: currency_code || "USD",
        });

        let text = this.$t(
          "reports.last_year_campaign_stats",
          "[{campaignID}] On {pastCampaignDate} {pastCampaignTime} you had {total_donors} donors and a total of {total}.",
          {
            campaignID: campaign_id,
            pastCampaignDate: pastCampaignDateFmt,
            pastCampaignTime: pastCampaignTimeFmt,
            total_donors: numFmt.format(total_donors),
            total: currencyFmt.format(total),
          }
        );

        if (after_total > 0 && after_total_donors > 0) {
          text += this.$t(
            "reports.after_stats",
            " After {pastCampaignDate} {pastCampaignTime} and to the end of campaign you had {after_total_donors} donors and a total of {after_total}.",
            {
              campaignID: campaign_id,
              pastCampaignDate: pastCampaignDateFmt,
              pastCampaignTime: pastCampaignTimeFmt,
              after_total_donors: numFmt.format(after_total_donors),
              after_total: currencyFmt.format(after_total),
            }
          );
        }

        res.push(text);
      }

      return res;
    },

    fetch() {
      if (!this.pastCampaignList.length) {
        this.stats = null;
        return;
      }

      const params = {
        past_campaign_id: this.pastCampaignList.map((c) => c.id),
      };

      fetchCompareToLastYear(this.oid, this.campaignId, params)
        .then(({ data, meta }) => {
          this.stats = data;
          this.meta = meta;
        })
        .catch(({ errors }) => {
          this.$bvToast.toast(errors[0].message, {
            title: this.$t("common.error", "Error"),
            variant: "danger",
            autoHideDelay: 5000,
          });
          this.stats = null;
        });
    },
  },
};
</script>

<style scoped>
.controls-row {
  @apply tw-flex tw-items-center tw-mb-3 tw-gap-2;
}
</style>
